/**
 * API wrapper for the Kontrollgaenge module (Phase 5 Stufe G — Frontend
 * Slice 3, 2026-05-02). Recurring inspection rounds against an Objekt;
 * decoupled from the Auftrags-Strang because they are routine
 * documentation work, not work orders.
 */
import { api } from './api';

export type KontrollgangResultStatus = 'ok' | 'mangel' | 'nicht_zutreffend';

export interface ChecklistItem {
  id: string;
  label: string;
  optional?: boolean;
}

interface ChecklistResult {
  status: KontrollgangResultStatus;
  notiz?: string;
}

interface Checkliste {
  id: string;
  name: string;
  itemsJsonb: ChecklistItem[];
  aktiv: boolean;
  erstelltAm: string;
}

interface KontrollgangFotoMeta {
  id: string;
  itemId: string | null;
  dateiname: string;
  contentType: string;
  bytesSize: number;
}

export interface Kontrollgang {
  id: string;
  objektId: string;
  checklisteId: string;
  kontrolliertAm: string;
  kontrolliertDurchUserId: string;
  ergebnisseJsonb: Record<string, ChecklistResult>;
  notiz: string | null;
  abgeschlossen: boolean;
  fotos?: KontrollgangFotoMeta[];
}

interface StartKontrollgangInput {
  objektId: string;
  checklisteId: string;
}

interface RecordResultInput {
  itemId: string;
  status: KontrollgangResultStatus;
  notiz?: string;
}

export const RESULT_LABEL: Record<KontrollgangResultStatus, string> = {
  ok: 'OK',
  mangel: 'Mangel',
  nicht_zutreffend: 'n.z.',
};

export const RESULT_BADGE: Record<KontrollgangResultStatus, string> = {
  ok: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  mangel: 'bg-rose-100 text-rose-800 border-rose-200',
  nicht_zutreffend: 'bg-slate-100 text-slate-700 border-slate-200',
};

async function uploadMultipart<T>(path: string, formData: FormData): Promise<T> {
  const csrfMatch = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('busch_erp_csrf='));
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (csrfMatch) headers['X-CSRF-Token'] = decodeURIComponent(csrfMatch.split('=')[1] ?? '');
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: formData,
  });
  const contentType = res.headers.get('content-type') ?? '';
  const payload: unknown = contentType.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string } })?.error;
    const e = new Error(err?.message ?? res.statusText) as Error & {
      status?: number;
      code?: string;
    };
    e.status = res.status;
    e.code = err?.code ?? 'HTTP_' + res.status;
    throw e;
  }
  return payload as T;
}

export const kontrollgaengeApi = {
  async listChecklisten(): Promise<{ items: Checkliste[] }> {
    return api<{ items: Checkliste[] }>('/api/v1/kontrollgaenge/checklisten');
  },

  async list(): Promise<{ items: Kontrollgang[] }> {
    return api<{ items: Kontrollgang[] }>('/api/v1/kontrollgaenge');
  },

  async getById(id: string): Promise<{ item: Kontrollgang }> {
    return api<{ item: Kontrollgang }>(`/api/v1/kontrollgaenge/${id}`);
  },

  async start(input: StartKontrollgangInput): Promise<{ item: Kontrollgang }> {
    return api<{ item: Kontrollgang }>('/api/v1/kontrollgaenge', { method: 'POST', body: input });
  },

  async recordResult(id: string, input: RecordResultInput): Promise<{ item: Kontrollgang }> {
    return api<{ item: Kontrollgang }>(`/api/v1/kontrollgaenge/${id}/result`, {
      method: 'POST',
      body: input,
    });
  },

  async finish(id: string, notiz?: string): Promise<{ item: Kontrollgang }> {
    return api<{ item: Kontrollgang }>(`/api/v1/kontrollgaenge/${id}/finish`, {
      method: 'POST',
      body: notiz ? { notiz } : {},
    });
  },

  async uploadFoto(
    id: string,
    file: File,
    itemId?: string,
  ): Promise<{ item: KontrollgangFotoMeta }> {
    const fd = new FormData();
    fd.append('foto', file, file.name);
    if (itemId) fd.append('itemId', itemId);
    return uploadMultipart<{ item: KontrollgangFotoMeta }>(
      `/api/v1/kontrollgaenge/${id}/fotos`,
      fd,
    );
  },

  fotoUrl(kontrollgangId: string, fotoId: string): string {
    return `/api/v1/kontrollgaenge/${kontrollgangId}/fotos/${fotoId}`;
  },
};
